import { useEffect, useState, useCallback } from "react";
import "./App.css";
import "./index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Chatbot from "./components/Chatbot";
import CookieBanner from "./components/CookieBanner";
import LeadCaptureModal from "./components/LeadCaptureModal";
import AnnouncementBanner from "./components/AnnouncementBanner";
import HomePage from "./pages/HomePage";
import UniversitiesPage from "./pages/UniversitiesPage";
import CompaniesPage from "./pages/CompaniesPage";
import AboutPage from "./pages/AboutPage";
import ProgrammesPage from "./pages/ProgrammesPage.tsx";
import CSProgrammePage from "./pages/CSProgrammePage.jsx";
import PlacementTracksPage from "./pages/PlacementTracksPage.jsx";
import CareersPage from "./pages/CareersPage";
import WaitlistPage from "./pages/WaitlistPage";
import LegalPage from "./pages/LegalPage";
import BTechEnrollmentPage from "./pages/BTechEnrollmentPage";
import SMRUPage from "./partners/smru/SMRUPage";
import SmruGuidancePage from "./partners/smru/SmruGuidancePage";
import SmruBrochurePage from "./partners/smru/SmruBrochurePage";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "instant",
        });
    }, [pathname]);
    return null;
}

/**
 * Standalone partner landing pages (e.g. /RFC) render with NO global chrome —
 * no Navbar, AnnouncementBanner, Footer, Chatbot, CookieBanner or LeadModal.
 * Match against these prefixes (case-insensitive) so a partner page owns the
 * entire viewport.
 */
const STANDALONE_PREFIXES = ["/rfc"];

function useIsStandalone() {
    const { pathname } = useLocation();
    const p = pathname.toLowerCase();
    return STANDALONE_PREFIXES.some((s) => p === s || p.startsWith(s + "/"));
}

function Shell() {
    const [bannerVisible, setBannerVisible] = useState(true);
    const [bannerHeight, setBannerHeight] = useState(44);
    const [leadModalKey, setLeadModalKey] = useState(0);
    const isStandalone = useIsStandalone();

    const handleApplyNow = useCallback(() => {
        setLeadModalKey((k) => k + 1);
    }, []);

    // Open the home-page lead modal whenever "/apply" (or "?apply") is in the URL.
    const { pathname, search } = useLocation();
    useEffect(() => {
        const p = pathname.toLowerCase();
        const hasApply =
            p === "/apply" ||
            p.endsWith("/apply") ||
            new URLSearchParams(search).has("apply");
        if (hasApply) setLeadModalKey((k) => k + 1);
    }, [pathname, search]);

    // Standalone partner pages: render the route on its own, full-bleed.
    if (isStandalone) {
        return (
            <Routes>
                <Route path="/RFC" element={<SMRUPage />} />
                <Route path="/RFC/guidance" element={<SmruGuidancePage />} />
                <Route path="/RFC/brochure" element={<SmruBrochurePage />} />
            </Routes>
        );
    }

    return (
        <>
            <AnnouncementBanner
                onVisibilityChange={setBannerVisible}
                onApplyNow={handleApplyNow}
                onHeightChange={setBannerHeight}
            />
            <Navbar bannerVisible={bannerVisible} bannerHeight={bannerHeight} />
            {/* Flow spacer: reserves the fixed banner's height so page
                content clears the (taller) header when the banner is shown,
                and collapses to 0 — back to normal — when it's dismissed. */}
            <div
                aria-hidden="true"
                style={{
                    height: bannerVisible ? bannerHeight : 0,
                    transition: "height 0.3s ease",
                }}
            />
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/programmes" element={<ProgrammesPage />} />
                <Route path="/cs-programme" element={<CSProgrammePage />} />
                <Route
                    path="/placement-tracks"
                    element={<PlacementTracksPage />}
                />
                {/* <Route
                    path="/universities"
                    element={<UniversitiesPage />}
                /> */}
                {/* <Route path="/companies" element={<CompaniesPage />} /> */}
                <Route path="/about" element={<AboutPage />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/waitlist" element={<WaitlistPage />} />
                <Route path="/btech-enroll" element={<BTechEnrollmentPage />} />
                <Route path="/legal/:slug" element={<LegalPage />} />
                <Route path="*" element={<HomePage />} />
            </Routes>
            <Footer />
            <Chatbot />
            <CookieBanner />
            <LeadCaptureModal
                key={leadModalKey}
                instantOpen={leadModalKey > 0}
            />
        </>
    );
}

function App() {
    return (
        <div className="App bg-background min-h-screen overflow-x-hidden">
            <BrowserRouter basename="/">
                <ScrollToTop />
                <Shell />
                <Toaster position="bottom-right" richColors />
            </BrowserRouter>
        </div>
    );
}

export default App;
